import { useCallback, useEffect, useState } from "react"
import useAnimateIn from "../../hooks/useAnimateIn"
import API from "../../api"
import SkeletonComponent from "../../components/skeleton/skeleton.component"
import { Link, useParams } from "react-router-dom"
import "./test-detail.page.css"
import moment from "moment"
import { mean, standardDeviation } from "simple-statistics"
import { bin, range, scaleLinear } from "d3"
import {
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  XAxis,
  YAxis,
  Line,
  ComposedChart,
} from "recharts"
import { Polyfit } from "../../utils/polyfit"
import { logoMarkIcon } from "../../icons"
import { usePDF } from "react-to-pdf"
import { ToastContainer, Toast, toast } from "react-toastify"

const TestDetailPage = (props) => {
  const { user, userMeta } = props
  const params = useParams()
  const [loading, setLoading] = useState(true)
  const [test, setTest] = useState([])
  const [chartData, setChartData] = useState()
  const [notes, setNotes] = useState([])
  const [newNote, setNewNote] = useState("")
  const { toPDF, targetRef } = usePDF({ filename: `test-${params.testId}.pdf` })

  useAnimateIn()

  const createChartDataForMetric = useCallback((data, metric) => {
    const dataSubset = [...data]
      .filter((i) => parseFloat(i[metric]) > 0.0 && parseFloat(i[metric]) < 1.0)
      .map((i) => parseFloat(i[metric]))

    const bins = createHistogram(dataSubset)
    let totalCount = 0
    let binCounts = []
    const xVals = []
    const yVals = []
    bins.forEach((a) => {
      totalCount += a.length
      binCounts.push({ x: a.x0, count: a.length })
      xVals.push(a.x0)
      yVals.push(a.length)
    })

    // Convert to percentage
    binCounts = binCounts.map((c) => {
      return { x: c.x, y: (c.count / totalCount) * 100 }
    })

    // Create polyfit line
    const poly = new Polyfit(xVals, yVals)
    const solver = poly.getPolynomial(2)
    const xMin = Math.min(...xVals)
    const xMax = Math.max(...xVals)
    const xValsLinSpaceGen = scaleLinear([xMin, xMax], [0, 100])
    const xValsLinSpace = xValsLinSpaceGen.ticks(100)
    const lineVals = []
    xValsLinSpace.forEach((x) => {
      const val = solver(x)
      lineVals.push({ x, y: val })
    })

    return {
      data: binCounts,
      line: lineVals,
    }
  }, [])

  // Fetch on load
  useEffect(() => {
    const fetchTests = async () => {
      try {
        const testRes = await API.makeGetRequest(user, `tests/${params.testId}`)
        console.log(testRes)
        setTest(testRes)
        setNotes(testRes.notes)

        // create charts
        if (testRes.status === "Ready") {
          const activation = createChartDataForMetric(
            testRes.cellData,
            "activationNkg2d"
          )

          const cytotoxicity = createChartDataForMetric(
            testRes.cellData,
            "cytotoxicityCd107a"
          )

          const car = createChartDataForMetric(testRes.cellData, "car")

          // Create relative persistence
          const persistenceScale = scaleLinear().domain([-8, 0]).range([0, 100])
          const relativePersistence = persistenceScale(testRes.persistenceDial)
          setChartData({ activation, cytotoxicity, car, relativePersistence })
        }
      } catch {
        // todo handle error
      } finally {
        setLoading(false)
      }
    }

    if (user) {
      fetchTests()
    }
  }, [user, params, createChartDataForMetric])

  const createHistogram = (arr, numBuckets = 10) => {
    const generator = bin().thresholds((data, min, max) => {
      return range(numBuckets).map((t) => min + (t / numBuckets) * (max - min))
    })
    const bins = generator(arr)
    return bins
  }

  const getCellData = (attr) => {
    return test.cellData.map((c) => parseFloat(c[attr]))
  }

  const handleNewNote = async () => {
    try {
      const noteBody = {
        body: newNote,
        createdBy: userMeta.fullName,
      }

      const notesRes = await API.makePostRequest(
        user,
        `tests/${params.testId}/notes`,
        noteBody
      )

      setNewNote("")
      setNotes([{ ...noteBody, id: notesRes.noteId }, ...notes])
      toast.success("Your notes have been submitted successfully!")
    } catch (e) {
      // do something
    }
  }

  return (
    <div className="cb-page">
      <ToastContainer position="top-center" theme="dark" />
      <div className="cb-page-header">
        <input type="text" placeholder="Search..." />
      </div>
      <div className="cb-page-content cb-test-result">
        <div className="cb-test-left" ref={targetRef}>
          <div className="cb-test-header">
            <h3>Test: {params.testId}</h3>
            <button className="cb-primary-btn" onClick={() => toPDF()}>
              Export
            </button>
          </div>
          <ul className="cb-breadcrumbs">
            <li>
              <Link to="/tests">Tests</Link>
            </li>
            <li>{params.testId}</li>
          </ul>
          {loading && <SkeletonComponent />}
          {!loading && test && (
            <>
              <div className="cb-test-group">
                <div className="cb-test-rows with-border">
                  <div className="cb-test-row">
                    Patient ID: {test.patientId}
                  </div>
                  <div className="cb-test-row">
                    Clinical Trial ID: {test.trialId}
                  </div>
                </div>
                <div className="cb-test-rows">
                  <div className="cb-test-row">
                    Blood Collected:{" "}
                    {moment(test.bloodCollectionDate).format("MM/DD/YYYY")}
                  </div>
                  <div className="cb-test-row">
                    Results Available:{" "}
                    {test.resultDate
                      ? moment(test.resultDate).format("MM/DD/YYYY")
                      : "Not ready yet..."}
                  </div>
                </div>
              </div>
              {chartData && (
                <>
                  <div className="cb-test-group with-background">
                    <div className="cb-test-rows">
                      <div className="cb-test-row">
                        Total Cells: <strong>{test.cellData?.length}</strong>
                      </div>
                    </div>
                    <div className="cb-test-rows">
                      <div className="cb-test-row">
                        Cell Persistence:{" "}
                        <strong>
                          {parseFloat(test.persistence).toFixed(6)}
                        </strong>
                      </div>
                    </div>
                  </div>
                  <div className="cb-test-persistence">
                    <h6>
                      Relative Persistence <span>(Trial Cohort)</span>
                    </h6>
                    <div className="cb-persistence-gauge">
                      <div className="cb-persistence-section poor">
                        <div className="cb-fill" /> Poor
                      </div>
                      <div className="cb-persistence-section fair">
                        <div className="cb-fill" /> Fair
                      </div>
                      <div className="cb-persistence-section good">
                        <div className="cb-fill" /> Good
                      </div>
                      <div
                        className="cb-persistence-indicator"
                        style={{ left: `${chartData.relativePersistence}%` }}
                      />
                    </div>
                  </div>
                  <div className="cb-test-charts">
                    <div className="cb-test-chart">
                      <div className="cb-chart-header">
                        <h6>Activation</h6>
                        <h6>
                          {mean(getCellData("activationNkg2d")).toFixed(2)} ±{" "}
                          {standardDeviation(
                            getCellData("activationNkg2d")
                          ).toFixed(2)}
                        </h6>
                      </div>
                      <div className="cb-chart-container">
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart
                            margin={{
                              right: 20,
                              left: -20,
                            }}
                          >
                            <CartesianGrid />
                            <XAxis type="number" dataKey="x" xAxisId={1} />
                            <XAxis type="number" dataKey="x" xAxisId={2} hide />
                            <YAxis type="number" dataKey="y" />
                            <Scatter
                              xAxisId={1}
                              data={chartData.activation.data}
                              fill="#F36A6A"
                            />
                            <Line
                              xAxisId={2}
                              dataKey="y"
                              data={chartData.activation.line}
                              stroke="#F36A6A"
                              strokeWidth={2}
                              dot={false}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="cb-test-chart">
                      <div className="cb-chart-header">
                        <h6>Cytotoxicity (CD107a)</h6>
                        <h6>
                          {mean(getCellData("cytotoxicityCd107a")).toFixed(2)} ±{" "}
                          {standardDeviation(
                            getCellData("cytotoxicityCd107a")
                          ).toFixed(2)}
                        </h6>
                      </div>
                      <div className="cb-chart-container">
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart
                            margin={{
                              right: 20,
                              left: -20,
                            }}
                          >
                            <CartesianGrid />
                            <XAxis type="number" dataKey="x" xAxisId={1} />
                            <XAxis type="number" dataKey="x" xAxisId={2} hide />
                            <YAxis type="number" dataKey="y" />
                            <Scatter
                              xAxisId={1}
                              data={chartData.cytotoxicity.data}
                              fill="#18C118"
                            />
                            <Line
                              xAxisId={2}
                              dataKey="y"
                              data={chartData.cytotoxicity.line}
                              stroke="#18C118"
                              strokeWidth={2}
                              dot={false}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="cb-test-chart">
                      <div className="cb-chart-header">
                        <h6>CAR</h6>
                        <h6>
                          {mean(getCellData("car")).toFixed(2)} ±{" "}
                          {standardDeviation(getCellData("car")).toFixed(2)}
                        </h6>
                      </div>
                      <div className="cb-chart-container">
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart
                            margin={{
                              right: 20,
                              left: -20,
                            }}
                          >
                            <CartesianGrid />
                            <XAxis type="number" dataKey="x" xAxisId={1} />
                            <XAxis type="number" dataKey="x" xAxisId={2} hide />
                            <YAxis type="number" dataKey="y" />
                            <Scatter
                              xAxisId={1}
                              data={chartData.car.data}
                              fill="#8764F7"
                            />
                            <Line
                              xAxisId={2}
                              dataKey="y"
                              data={chartData.car.line}
                              stroke="#8764F7"
                              strokeWidth={2}
                              dot={false}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <div className="cb-test-right">
          <h5>Notes</h5>
          {!loading && (
            <ul>
              {notes &&
                notes.map((n) => {
                  const { id, createdBy, body, created } = n
                  return (
                    <li key={id}>
                      <div className="cb-note-header">
                        <div className="cb-avatar with-bg">{createdBy[0]}</div>
                        <div className="cb-note-info">
                          {createdBy}
                          <h6>{moment(created).fromNow()}</h6>
                        </div>
                      </div>
                      <div className="cb-note-body">{body}</div>
                    </li>
                  )
                })}
              {test.resultDate && (
                <li>
                  <div className="cb-note-header">
                    <div className="cb-avatar">{logoMarkIcon} </div>
                    <div className="cb-note-info">
                      CellsBin
                      <h6>{moment(test.resultDate).fromNow()}</h6>
                    </div>
                  </div>
                  <div className="cb-note-body">
                    Results published on{" "}
                    {moment(test.resultDate).format("MM/DD/YYYY hh:mm a")}
                  </div>
                </li>
              )}
              {test.bloodCollectionDate && (
                <li>
                  <div className="cb-note-header">
                    <div className="cb-avatar">{logoMarkIcon} </div>
                    <div className="cb-note-info">
                      CellsBin
                      <h6>{moment(test.bloodCollectionDate).fromNow()}</h6>
                    </div>
                  </div>
                  <div className="cb-note-body">
                    Sample collected on{" "}
                    {moment(test.bloodCollectionDate).format(
                      "MM/DD/YYYY hh:mm a"
                    )}
                  </div>
                </li>
              )}
              <li>
                <div className="cb-note-header">
                  <div className="cb-avatar">{logoMarkIcon} </div>
                  <div className="cb-note-info">
                    CellsBin
                    <h6>{moment(test.orderDate).fromNow()}</h6>
                  </div>
                </div>
                <div className="cb-note-body">Test ordered</div>
              </li>
            </ul>
          )}
          {!loading && (
            <div className="cb-add-note">
              <textarea
                placeholder="Add Note..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <button
                className="cb-primary-btn"
                disabled={!newNote}
                onClick={handleNewNote}
              >
                Send
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TestDetailPage
